import type { UserInfo, UserConfig } from '/#/store'

import { defineStore } from 'pinia'
import { store } from '/@/store'
import { ref, computed } from 'vue'
import { RoleEnum } from '/@/enums/roleEnum'
import { ROLES_KEY, TOKEN_KEY, USER_INFO_KEY } from '/@/enums/cacheEnum'
import { clearAuthCache, getAuthCache, setAuthCache } from '/@/utils/auth'
import { getUserInfo, getUserPermission } from '/@/api/sys/user'
import { useI18n } from '/@/hooks/web/useI18n'
import { useMessage } from '/@/hooks/web/useMessage'
import { h } from 'vue'
import { AUTH_CODE_TO_URL_MAP } from '/@/enums/authEnum'
import { router } from '/@/router/index'
import { removeDBApiToken } from '/@/utils/auth/dbapi'

export const useUserStore = defineStore('app-user', () => {
  // State
  const userInfo = ref<Nullable<UserInfo>>({
    userId: 1,
  })
  const token = ref<string | undefined>('1233')
  const roleList = ref<RoleEnum[]>([])
  const sessionTimeout = ref<boolean | undefined>(false)
  const lastUpdateTime = ref(0)
  const homePath = ref<string | undefined>(undefined)
  const allRoutes = ref<[]>([])
  const menuRoutes = ref<[]>([])
  const permissionEnum = ref<[]>([])
  const userConfig = ref<UserConfig>({})
  const unlock = ref<any>(null)
  const dpList = ref<Recordable[]>([])
  const supervisor = ref(false)

  // Getters
  const getUserInfoData = computed(
    () => userInfo.value || getAuthCache<UserInfo>(USER_INFO_KEY) || {},
  )
  const getToken = computed(() => token.value || getAuthCache<string>(TOKEN_KEY))
  const getRoleList = computed(() =>
    roleList.value.length > 0 ? roleList.value : getAuthCache<RoleEnum[]>(ROLES_KEY),
  )
  const getSessionTimeout = computed(() => !!sessionTimeout.value)
  const getLastUpdateTime = computed(() => lastUpdateTime.value)
  const getHomePath = computed(() => homePath.value)
  const getAllRoutes = computed(() => allRoutes.value || [])
  const getMenuRoutes = computed(() => menuRoutes.value || [])
  const getPermissionEnum = computed<string[]>(() => permissionEnum.value || [])
  const getUserConfig = computed(() => userConfig.value || {})
  const getUnlock = computed(() => unlock.value)
  const getDpList = computed(() => dpList.value)

  // Actions
  function setUnlock(unlockValue: any) {
    unlock.value = unlockValue
  }

  function setToken(info: string | undefined) {
    token.value = info ? info : '' // for null or undefined value
    setAuthCache(TOKEN_KEY, info)
  }

  function setRoleList(list: RoleEnum[]) {
    roleList.value = list
    setAuthCache(ROLES_KEY, list)
  }

  function setUserInfo(info: UserInfo | null) {
    userInfo.value = info
    lastUpdateTime.value = new Date().getTime()
    dpList.value = info?.dpList || []
    setAuthCache(USER_INFO_KEY, info)
    // 同步更新 localStorage，确保路由守卫能立即获取
    if (info) {
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(info))
    }
  }

  function clearUserInfo() {
    userInfo.value = null
    localStorage.removeItem(USER_INFO_KEY)
    clearAuthCache()
    removeDBApiToken()
  }

  function setSessionTimeout(flag: boolean) {
    sessionTimeout.value = flag
  }

  function setHomePath(path: string) {
    homePath.value = path
  }

  function setDpList(list: Recordable[]) {
    dpList.value = list
  }

  function resetState() {
    userInfo.value = null
    token.value = ''
    roleList.value = []
    sessionTimeout.value = false
  }

  async function getUserInfoAction() {
    return {}
    if (!getToken.value) return null
    try {
      const info = await getUserInfo()
      setUserInfo(info)
      return info
    } catch (error: any) {
      router.push({
        path: '/permission-exception',
        query: { code: error.code, type: 'userInfo' },
      })
      throw error
    }
  }

  /**
   * @description: logout
   */
  async function logout(goLogin = false) {
    setToken(undefined)
    setSessionTimeout(false)
    clearUserInfo()
    if (goLogin) {
      location.reload()
    }
  }

  /**
   * @description: Confirm before logging out
   */
  function confirmLoginOut() {
    const { createConfirm } = useMessage()
    const { t } = useI18n()
    createConfirm({
      iconType: 'warning',
      title: () => h('span', t('sys.app.logoutTip')),
      content: () => h('span', t('sys.app.logoutMessage')),
      onOk: async () => {
        await logout(true)
      },
    })
  }

  function setAllRoutes(routes: any) {
    allRoutes.value = routes || []
  }

  function setPermissionEnum(permission: any) {
    permissionEnum.value = permission || []
  }

  /**
   * @description: 拉取权限信息
   */
  async function getPermissionRoutesAction() {
    try {
      const resPermission = await getUserPermission()
      if (userInfo.value?.supervisor) {
        resPermission.push(RoleEnum.ADMIN)
      }
      const permission: string[] = []
      resPermission?.forEach((item) => {
        if (AUTH_CODE_TO_URL_MAP[item]) {
          permission.push(
            ...(Array.isArray(AUTH_CODE_TO_URL_MAP[item])
              ? AUTH_CODE_TO_URL_MAP[item]
              : [AUTH_CODE_TO_URL_MAP[item]]),
          )
        }
      })
      // 如果没有菜单权限，可跳转到无权限页面或者404等，业务逻辑自行处理，返回了[]过滤出有权限的路由也为[]，路由守卫统一处理了跳转到无权限页面
      // if (!permission || (permission && permission.length === 0)) {
      //   router.replace({ path: '/unauth' }) // 路由守卫统一处理了
      //   return []
      // }

      // 按钮操作和tab是否展示的权限
      setPermissionEnum(resPermission)
      console.log('code映射匹配到的路由', permission)
      // 匹配路由权限
      return permission
    } catch (error: any) {
      router.push({
        path: '/permission-exception',
        query: { code: error.code, type: 'permission' },
      })
      throw error
    }
  }

  function setUserConfig(config: UserConfig) {
    userConfig.value = config || {}
  }

  return {
    userInfo,
    token,
    roleList,
    sessionTimeout,
    lastUpdateTime,
    homePath,
    allRoutes,
    menuRoutes,
    permissionEnum,
    userConfig,
    unlock,
    dpList,
    supervisor,
    getUserInfo: getUserInfoData,
    getToken,
    getRoleList,
    getSessionTimeout,
    getLastUpdateTime,
    getHomePath,
    getAllRoutes,
    getMenuRoutes,
    getPermissionEnum,
    getUserConfig,
    getUnlock,
    getDpList,
    setUnlock,
    setToken,
    setRoleList,
    setUserInfo,
    clearUserInfo,
    setSessionTimeout,
    setHomePath,
    setDpList,
    resetState,
    getUserInfoAction,
    logout,
    confirmLoginOut,
    setAllRoutes,
    setPermissionEnum,
    getPermissionRoutesAction,
    setUserConfig,
  }
})

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store)
}
